import React, { useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"

import { Popover } from "@productlane/ui"
import {
	Command,
	CommandAddNewItem,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { getProjectIcon } from "@/lib/projects"
import { mutateOk, useZero } from "@/lib/zero"
import { ChangelogTagSelectTrigger } from "./changelog-tag-select-trigger"
import { CreateChangelogTagDialog } from "./create-changelog-tag-dialog"

interface ChangelogTagSelectProps {
	changelogId: string
	linkedTagIds: string[]
}

export const ChangelogTagSelect = React.memo(
	({ changelogId, linkedTagIds }: ChangelogTagSelectProps) => {
		const zero = useZero()
		const [allTags] = useQuery(queries.changelogTags())
		const [isOpen, setIsOpen] = useState(false)
		const [isDialogOpen, setDialogOpen] = useState(false)
		const [searchQuery, setSearchQuery] = useState("")

		const filteredTags = allTags.filter((t) =>
			t.name.toLowerCase().includes(searchQuery.trim().toLowerCase()),
		)

		const handleToggleTag = async (tagId: string) => {
			await mutateOk(
				zero.mutate(
					mutators.changelogTags.linkTag({
						changelogId,
						tagId,
					}),
				),
			)
		}

		// Labels are created in settings; with none there, the picker has nothing to offer.
		if (allTags.length === 0) return null

		return (
			<>
				<CreateChangelogTagDialog
					isOpen={isDialogOpen}
					initialName={searchQuery}
					onOpenChange={(open, created) => {
						if (!open && created) {
							void zero.mutate(
								mutators.changelogTags.linkTag({
									changelogId,
									tagId: created.id,
								}),
							).client
							setSearchQuery("")
						}
						setDialogOpen(open)
					}}
				/>
				<Popover
					open={isOpen}
					onOpenChange={setIsOpen}
					className="p-0"
					triggerTooltip={linkedTagIds.length > 0 ? "Add label" : undefined}
					content={
						<Command shouldFilter={false}>
							<CommandInput
								autoFocus
								placeholder="Search or create label..."
								value={searchQuery}
								onValueChange={setSearchQuery}
							/>
							<CommandList>
								<CommandAddNewItem
									onCreate={() => {
										setDialogOpen(true)
										setIsOpen(false)
									}}
									show={true}
									search={searchQuery || "label"}
									wrapperClassName="px-2"
								/>
								{filteredTags.length > 0 && (
									<CommandGroup className="px-2">
										{filteredTags.map((tag) => {
											const isLinked = linkedTagIds.includes(tag.id)
											const Icon = tag.icon ? getProjectIcon(tag.icon) : null
											return (
												<CommandItem
													key={tag.id}
													value={tag.id}
													onSelect={() => void handleToggleTag(tag.id)}
												>
													<div className="flex items-center gap-2">
														{Icon ? (
															<Icon
																className="h-3 w-3"
																style={{ color: tag.color }}
															/>
														) : (
															<div
																className="h-2.5 w-2.5 rounded-full"
																style={{ backgroundColor: tag.color }}
															/>
														)}
														<span className="flex-1 truncate">{tag.name}</span>
														{isLinked && <Check className="h-4 w-4" />}
													</div>
												</CommandItem>
											)
										})}
									</CommandGroup>
								)}
							</CommandList>
						</Command>
					}
				>
					<ChangelogTagSelectTrigger hasTags={linkedTagIds.length > 0} />
				</Popover>
			</>
		)
	},
)
